'use client';

import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import GlobalStyles from '@mui/material/GlobalStyles';
import { IconButton, Tooltip, alpha } from '@mui/material';
import { LightMode, DarkMode } from '@mui/icons-material';
import { useState, useContext } from 'react';
import { UIContext } from '../../context/UI.context';
import { signInUserWithEmailAndPassword, resetPassword } from '../../utils/core';
import { useNavigate } from 'react-router-dom';
import LoginForm from './LoginForm';
import AnimatedBackground from './AnimatedBackground';
import { useAppTheme } from '../../context/theme.context';

const SignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [result, setResult] = useState({ success: 'yes' });
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [resetResult, setResetResult] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Iniciando sesión...');

  const { loading, setLoading } = useContext(UIContext);
  const { isDark, setThemeMode, colors } = useAppTheme();

  // No redirigir automáticamente - mejor UX con loading en formulario

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    validateForm(value, password);
    if (fieldErrors.email || fieldErrors.password) {
      setFieldErrors({ email: '', password: '' });
    }
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    validateForm(email, value);
    if (fieldErrors.password) {
      setFieldErrors((prev) => ({ ...prev, password: '' }));
    }
  };

  const validateForm = (emailValue, passwordValue) => {
    const isValid =
      emailValue &&
      emailValue.length > 0 &&
      passwordValue &&
      passwordValue.length > 0;
    setIsFormValid(isValid);
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = async () => {
    if (!email || email.trim() === '') {
      setResetResult({
        success: 'no',
        message: 'Por favor, ingresa tu correo electrónico primero',
      });
      return;
    }

    const result = await resetPassword(email);
    setResetResult(result);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setLoadingMessage('Iniciando sesión...');
    
    try {
      setFieldErrors({ email: '', password: '' });
      await signInUserWithEmailAndPassword(email, password);
      setResult({ success: 'yes' });
      
      // Cambiar mensajes progresivamente para mejor UX
      setLoadingMessage('Verificando credenciales...');
      
      setTimeout(() => {
        setLoadingMessage('Cargando tu perfil...');
      }, 800);
      
      setTimeout(() => {
        setLoadingMessage('Preparando la aplicación...');
      }, 1400);
      
      // Mantener el loading por 2 segundos en el formulario para mejor UX
      setTimeout(() => {
        navigate('/');
        setLoading(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error en login:', error);
      // Asignar error al campo correspondiente en lugar de modal/alerta
      if (error.code === 'auth/invalid-email') {
        setFieldErrors({ email: 'Formato de email inválido', password: '' });
      } else if (error.code === 'auth/user-not-found') {
        setFieldErrors({ email: 'No existe una cuenta con este email', password: '' });
      } else if (error.code === 'auth/wrong-password') {
        setFieldErrors({ email: '', password: 'Contraseña incorrecta' });
      } else if (error.code === 'auth/invalid-credential') {
        setFieldErrors({ email: '', password: 'Email o contraseña incorrectos' });
      } else if (error.code === 'auth/too-many-requests') {
        setFieldErrors({ email: '', password: 'Muchos intentos fallidos. Pruebe más tarde.' });
      } else {
        setFieldErrors({ email: '', password: 'Error de conexión. Intente nuevamente.' });
      }
      setResult({ success: 'no' });
      setLoading(false);
      setLoadingMessage('Iniciando sesión...');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDark ? '#080808' : colors.background.page,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
      }}
    >
      <CssBaseline />
      <GlobalStyles styles={{ 
        body: { backgroundColor: `${isDark ? '#080808' : colors.background.default} !important` }, 
        html: { backgroundColor: `${isDark ? '#080808' : colors.background.default} !important` },
        '#root': { backgroundColor: `${isDark ? '#080808' : colors.background.default} !important` }
      }} />

      <AnimatedBackground />

      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 2,
        }}
      >
        <Tooltip title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}>
          <IconButton
            onClick={() => setThemeMode(isDark ? 'light' : 'dark')}
            aria-label="Cambiar tema"
            sx={{
              color: isDark ? '#fff' : colors.textDark,
              border: `1px solid ${isDark ? alpha('#ffffff', 0.2) : colors.borderLight}`,
              backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha(colors.surface, 0.85),
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: isDark ? alpha('#ffffff', 0.14) : alpha(colors.surface, 1),
              },
            }}
          >
            {isDark ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Tooltip>
      </Box>

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <LoginForm
            fieldErrors={fieldErrors}
            email={email}
            password={password}
            showPassword={showPassword}
            result={result}
            resetResult={resetResult}
            isFormValid={isFormValid}
            loading={loading}
            loadingMessage={loadingMessage}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onShowPasswordClick={handleClickShowPassword}
            onForgotPassword={handleForgotPassword}
            onSubmit={handleSubmit}
            isDark={isDark}
            colors={colors}
          />
      </Container>
    </Box>
  );
};

export default SignIn;
